import React from 'react';
import clsx from 'clsx';
import { SVGIcon } from '../SVGIcon/SVGIcon';
import { IconName } from '../SVGIcon/SVGIcon.types';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'warning'
  | 'outline-white'
  | 'gold'
  | 'gold-outline';
export type ButtonSize = 'small' | 'medium' | 'large';

type BaseButtonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName | React.ReactNode;
  iconColor?: string;
  iconPosition?: 'left' | 'right';
  isLoading?: boolean;
  /**
   * Optional label shown next to the spinner while the button is loading.
   * Falls back to `children` when not provided.
   */
  loadingLabel?: React.ReactNode;
  fullWidth?: boolean;
  children: React.ReactNode;
  className?: string;
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => void | Promise<unknown>;
  disabled?: boolean;
};

type ButtonAsButton = BaseButtonProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseButtonProps> & {
    as?: 'button';
    href?: never;
  };

type ButtonAsLink = BaseButtonProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseButtonProps> & {
    as: 'a';
    href: string;
  };

export type ButtonProps = ButtonAsButton | ButtonAsLink;

const variantStyles: Record<ButtonVariant, string> = {
  primary: 'bg-blue text-white hover:bg-blue-600 focus:ring-blue-500',
  secondary: 'bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500',
  success: 'bg-green text-white hover:bg-green-600 focus:ring-green-500',
  danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
  warning: 'bg-yellow-500 text-white hover:bg-yellow-600 focus:ring-yellow-500',
  'outline-white':
    'bg-transparent text-white border border-white hover:bg-white/10 focus:ring-white',
  gold: 'bg-[#CA9A39] border-none text-white hover:bg-[#CA9A39]/80 focus:ring-[#CA9A39]',
  'gold-outline':
    'bg-transparent text-[#FCE2AC] border border-[#FCE2AC] hover:bg-gold/10 focus:ring-gold',
};

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  icon,
  iconPosition = 'left',
  isLoading = false,
  loadingLabel,
  fullWidth = false,
  disabled = false,
  children,
  className,
  iconColor = '',
  onClick,
  as = 'button',
  ...props
}) => {
  const [autoLoading, setAutoLoading] = React.useState(false);
  const showLoading = isLoading || autoLoading;

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    if (disabled || showLoading) {
      event.preventDefault();
      return;
    }
    const result = onClick?.(event);
    if (result && typeof (result as Promise<unknown>).then === 'function') {
      setAutoLoading(true);
      (result as Promise<unknown>).finally(() => setAutoLoading(false));
    }
  };

  const baseStyles =
    'inline-flex items-center justify-center rounded-[50px] font-semibold tracking-[0.2px] transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

  const sizeStyles: Record<ButtonSize, string> = {
    small: 'px-2 py-1.5 text-xs lg:px-3 lg:py-1.5 lg:text-sm',
    medium: `pl-[22px] py-[12px] text-sm lg:text-base lg:pl-[36px] lg:py-[15px] ${icon && iconPosition === 'right' ? 'pr-4' : 'pr-[36px]'}`,
    large: 'lg:px-6 px-4 py-3 text-sm lg:text-base lg:py-3 lg:text-lg',
  };

  const content = (
    <>
      {autoLoading ? (
        <>
          <span>{loadingLabel ?? children}</span>&nbsp;
          <SVGIcon name="loading-circle" fill={iconColor} className="mr-3 animate-spin" />
        </>
      ) : (
        <>
          {icon &&
            iconPosition === 'left' &&
            (typeof icon === 'string' ? (
              <SVGIcon name={icon as IconName} className="mr-4" />
            ) : (
              icon
            ))}
          {children}
          {icon &&
            iconPosition === 'right' &&
            (typeof icon === 'string' ? (
              <SVGIcon name={icon as IconName} fill={iconColor} className="pr-4 ml-5" />
            ) : (
              icon
            ))}
        </>
      )}
    </>
  );

  const commonProps = {
    className: clsx(
      baseStyles,
      variantStyles[variant],
      sizeStyles[size],
      fullWidth && 'w-full',
      className,
    ),
    'aria-disabled': disabled || showLoading,
    'aria-busy': showLoading,
  };

  if (as === 'a') {
    const linkProps = props as React.AnchorHTMLAttributes<HTMLAnchorElement>;
    return (
      <a
        {...linkProps}
        {...commonProps}
        href={disabled || showLoading ? '#' : linkProps.href}
        onClick={handleClick}
      >
        {content}
      </a>
    );
  }

  const buttonProps = props as React.ButtonHTMLAttributes<HTMLButtonElement>;
  return (
    <button
      {...buttonProps}
      {...commonProps}
      onClick={handleClick}
      disabled={disabled || showLoading}
    >
      {content}
    </button>
  );
};

/*
// Jako przycisk (domyślnie)
<Button onClick={() => console.log('Kliknięto!')}>
  Kliknij mnie
</Button>

// Jako link
<Button as="a" href="/strona">
  Przejdź do strony
</Button>

// Link z ikoną
<Button as="a" href="/strona" icon="arrow-right" iconPosition="right">
  Przejdź dalej
</Button>

// Link z obsługą kliknięcia
<Button 
  as="a" 
  href="/strona" 
  onClick={(e) => {
    e.preventDefault();
    // własna logika
  }}
>
  Własna obsługa kliknięcia
</Button>

// Wyłączony link
<Button as="a" href="/strona" disabled>
  Niedostępny link
</Button>

// Link w trakcie ładowania
<Button as="a" href="/strona" isLoading>
  Ładowanie...
</Button>
*/
